/**
 * Var gatewayns adress och token kommer ifrån.
 *
 * Tidigare: `VITE_GATEWAY_URL` och `VITE_GATEWAY_TOKEN` byggdes in i SPA:n.
 * SPA:n serveras före inloggning, så båda gick att läsa med ett anonymt anrop
 * mot scc.skylandai.se — adressen till gatewayn och nyckeln som öppnar den,
 * tillsammans i samma publika fil. Det enda som återstod som skydd var att
 * `alex.tail8a8e79.ts.net` bara går att nå från tailnetet.
 *
 * Nu frågar vi backenden efter inloggning i stället. Token hamnar aldrig i
 * bundlen, den ligger bara i minnet under sessionen, och den kan roteras på
 * VPS:en utan ett nytt frontend-bygge.
 *
 * Byggvariablerna finns kvar som fallback och används när rutten inte svarar:
 * lokal utveckling mot en gateway på egen maskin, och produktion innan
 * backendens miljövariabler är satta. Är de inte satta heller blir resultatet
 * samma "Ej ansluten" som förut, inte en krasch.
 */
import { API_BASE } from '../config';

export interface GatewayCredentials {
    url: string;
    token?: string;
}

const FALLBACK: GatewayCredentials = {
    url: import.meta.env.VITE_GATEWAY_URL || 'ws://127.0.0.1:18789',
    token: import.meta.env.VITE_GATEWAY_TOKEN || undefined,
};

/* En hämtning per sidladdning räcker — utan cache gör varje omöjlig
   återanslutning ett nytt anrop. Misslyckas den nollställs den så att nästa
   försök får fråga igen (t.ex. efter inloggning). */
let pending: Promise<GatewayCredentials> | null = null;

async function load(): Promise<GatewayCredentials> {
    const res = await fetch(`${API_BASE}/gateway/credentials`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as Partial<GatewayCredentials>;
    if (!data.url) throw new Error('Svaret saknar url');
    return { url: data.url, token: data.token || undefined };
}

export function getGatewayCredentials(): Promise<GatewayCredentials> {
    if (!pending) {
        pending = load().catch((err) => {
            // Logga aldrig svaret självt — det innehåller token.
            console.warn('[gateway] kunde inte hämta uppgifter, använder byggvärden:', String(err));
            pending = null;
            return FALLBACK;
        });
    }
    return pending;
}

/** Glöm hämtade uppgifter (utloggning, eller byte av operatör). */
export function resetGatewayCredentials(): void {
    pending = null;
}
